import { Loader2, Pause, Sparkles, CheckCircle2, GitBranch } from 'lucide-react'
import type { AgentEvent } from '../types'
import { PERSONAS } from '../lib/personas'
import type { AgentStatus } from '../hooks/useAgentRunState'

const SEGMENTS = [
  { key: 'ingest', title: 'Archie · Ingestão', step: 'archie.ingest' },
  { key: 'review', title: 'Dochie · Análise', step: 'dochie.review' },
  { key: 'synthesis', title: 'Archie · SDD', step: 'archie.synthesis' },
  { key: 'storm', title: 'Jobs · Event Storm', step: 'jobs.event_storm' },
  { key: 'diagrams', title: 'Van Gogh · Diagramas', step: 'vangogh.diagrams' },
  { key: 'proto', title: 'Van Gogh · Protótipo', step: 'vangogh.prototype' },
] as const

function stepTouched(events: AgentEvent[], step: string, kind: 'STEP_STARTED' | 'STEP_FINISHED'): boolean {
  return events.some((event) => {
    const type = event.type || event.event_type
    const name = event.stepName || event.step
    return type === kind && name === step
  })
}

interface RoomFooterProps {
  events: AgentEvent[]
  running: boolean
  paused: boolean
  pendingChanges: boolean
  newIdeas: number
  hasArtifacts: boolean
  ideasCount: number
  statuses: Record<string, AgentStatus>
  stepLabels: Record<string, { label: string }>
  elapsed: string
  error: string | null
  onRetry: () => void
  onDismissError: () => void
  onPrimary: () => void
  onOpenAgent: (id: string) => void
  openAgent: string | null
}

export default function RoomFooter(props: RoomFooterProps) {
  const {
    events, running, paused, pendingChanges, newIdeas, hasArtifacts, ideasCount,
    statuses, stepLabels, elapsed, error, onRetry, onDismissError, onPrimary, onOpenAgent, openAgent,
  } = props

  const activeIndex = SEGMENTS.findIndex((seg) => stepTouched(events, seg.step, 'STEP_STARTED') && !stepTouched(events, seg.step, 'STEP_FINISHED'))

  let title = 'Pronto'
  let sub = hasArtifacts ? 'artefatos refletem a última geração' : 'gere a documentação a partir das ideias'
  let color = 'var(--af-mut)'
  if (error && !running) {
    title = 'Erro'
    sub = error
    color = 'var(--af-bad)'
  } else if (paused) {
    title = 'Pausado'
    sub = 'aguardando sua decisão'
    color = 'var(--af-warn)'
  } else if (running) {
    const seg = SEGMENTS[activeIndex]
    title = activeIndex >= 0 ? `${activeIndex + 1}/6` : 'Gerando'
    sub = seg ? seg.title : 'preparando o pipeline'
    color = 'var(--af-acc)'
  } else if (pendingChanges) {
    title = 'Desatualizado'
    sub = newIdeas > 0 ? `${newIdeas} ideias novas desde a última geração` : 'há ideias novas desde a última geração'
    color = 'var(--af-warn)'
  } else if (hasArtifacts) {
    title = 'Atualizado'
    sub = 'todos os artefatos refletem as ideias'
    color = 'var(--af-ok)'
  }

  const primaryDisabled = running || ideasCount === 0
  const primaryLabel = paused
    ? 'Revisar decisão'
    : running
      ? 'Gerando…'
      : pendingChanges
        ? 'Atualizar docs'
        : hasArtifacts
          ? 'Gerar novamente'
          : 'Gerar documentação'
  const PrimaryIcon = paused ? GitBranch : running ? Loader2 : Sparkles

  return (
    <footer className="h-[62px] shrink-0 border-t border-line bg-surface flex items-center gap-3 px-3">
      <div className="w-[280px] shrink-0 flex flex-col gap-1.5 min-w-0">
        <div className="flex items-center gap-1.5 text-xs min-w-0">
          {paused ? <Pause size={13} style={{ color }} /> : running ? <Loader2 size={13} className="animate-spin" style={{ color }} /> : <CheckCircle2 size={13} style={{ color }} />}
          <span className="font-semibold shrink-0" style={{ color }}>{title}</span>
          <span className="text-muted truncate flex-1">{sub}</span>
          {running && elapsed && <span className="font-mono text-[11px] text-faint">{elapsed}</span>}
        </div>
        <div className="flex gap-0.5">
          {SEGMENTS.map((seg, i) => {
            const done = stepTouched(events, seg.step, 'STEP_FINISHED')
            const current = running && i === activeIndex
            return (
              <div
                key={seg.key}
                title={seg.title}
                className="flex-1 h-[5px] rounded-sm"
                style={{
                  background: done || current ? 'var(--af-acc)' : 'var(--af-ln)',
                  opacity: current ? 1 : done ? 0.9 : 1,
                  animation: current ? 'afpulse 1.2s ease-in-out infinite' : undefined,
                }}
              />
            )
          })}
        </div>
        {error && !running && (
          <div className="flex gap-2 text-[11px]">
            <button type="button" onClick={onRetry} className="text-bad font-semibold cursor-pointer">Tentar novamente</button>
            <button type="button" onClick={onDismissError} className="text-faint cursor-pointer">Dispensar</button>
          </div>
        )}
      </div>
      <div className="w-px self-stretch my-2.5 bg-line" />
      <div className="flex-1 min-w-0 flex gap-1.5">
        {PERSONAS.map((persona) => {
          const status = statuses[persona.id] || 'idle'
          const waiting = paused && (persona.id === 'jobs' || persona.id === 'vangogh' || persona.id === 'archie')
          const line = waiting
            ? persona.id === 'archie' ? 'Precisa de você' : 'Aguardando a decisão'
            : stepLabels[persona.id]?.label || 'Disponível'
          const open = openAgent === persona.id
          return (
            <button
              key={persona.id}
              type="button"
              title={persona.role}
              onClick={() => onOpenAgent(persona.id)}
              className="flex-1 min-w-0 flex items-center gap-2 px-2 py-1 rounded-lg border text-left cursor-pointer hover:bg-surface-2"
              style={{
                borderColor: open ? persona.color : waiting ? 'var(--af-warnb)' : 'var(--af-ln)',
                background: waiting ? 'var(--af-warns)' : 'var(--af-sf)',
              }}
            >
              <span
                className="relative w-7 h-7 rounded-lg text-white flex items-center justify-center shrink-0"
                style={{ background: persona.color, animation: status === 'working' ? 'afring 1.4s ease-out infinite' : undefined, ['--ring' as string]: persona.color }}
              >
                <persona.icon size={14} />
                <span
                  className="absolute -right-0.5 -bottom-0.5 w-2.5 h-2.5 rounded-full border-2 border-surface"
                  style={{ background: status === 'error' ? 'var(--af-bad)' : status === 'done' ? 'var(--af-ok)' : waiting ? 'var(--af-warn)' : persona.color }}
                />
              </span>
              <span className="min-w-0 hidden xl:flex flex-col">
                <span className="text-xs font-semibold">{persona.name}</span>
                <span className="text-[11px] truncate" style={{ color: waiting ? 'var(--af-warn)' : 'var(--af-mut)' }}>{line}</span>
              </span>
            </button>
          )
        })}
      </div>
      <button
        type="button"
        onClick={onPrimary}
        disabled={primaryDisabled}
        className="h-9 shrink-0 inline-flex items-center gap-1.5 px-3.5 rounded-lg text-[12.5px] font-semibold cursor-pointer disabled:cursor-not-allowed"
        style={{
          background: paused ? 'var(--af-warn)' : primaryDisabled ? 'var(--af-sf2)' : 'var(--af-acc)',
          color: primaryDisabled && !paused ? 'var(--af-fnt)' : '#fff',
        }}
      >
        <PrimaryIcon size={14} className={running ? 'animate-spin' : ''} />
        {primaryLabel}
      </button>
    </footer>
  )
}
